import axios from "axios";
import { config } from "../config/env";
import { API_ENDPOINT } from "./Endpoints/endpoints";
import { store } from "../index";
import { loginSuccess } from "../slices/auth/login/reducer";

const API_URL = config.api.baseUrl;

// Create a separate axios instance for refresh token calls
// This avoids interference from global interceptors in api_helper.ts
const refreshTokenAxios = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Track if a refresh is in progress to prevent concurrent refresh requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Refreshes the access token using the refresh token stored in localStorage
 * @returns Promise with new access token or throws error
 */
export const refreshAccessToken = async (): Promise<string> => {
  try {
    // Get refresh token from localStorage
    const authUserStr = localStorage.getItem("authUser");
    if (!authUserStr) {
      throw new Error("No authentication data found");
    }

    const authUser = JSON.parse(authUserStr);
    const refreshToken = authUser?.refresh;

    if (!refreshToken) {
      throw new Error("No refresh token found");
    }

    // Call refresh token endpoint using a separate axios instance
    // This prevents global interceptors from interfering with the refresh token call
    console.log("Calling refresh token endpoint:", `${API_URL}${API_ENDPOINT.AUTH.REFRESH_TOKEN}`);
    const response = await refreshTokenAxios.post(
      API_ENDPOINT.AUTH.REFRESH_TOKEN,
      { refresh: refreshToken }
    );

    console.log("Refresh token API response:", {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
    });

    // Handle different response structures
    let responseData = response.data;
    
    // Check if response is wrapped in data property (backend returns { status, success, message, data: {...} })
    if (responseData?.data) {
      responseData = responseData.data;
    }

    // Extract tokens and user data from response
    const access = responseData?.access || responseData?.accessToken;
    const refresh = responseData?.refresh || responseData?.refreshToken;
    const user_id = responseData?.user_id;
    const email = responseData?.email;
    const username = responseData?.username;
    const permissions = responseData?.permissions;

    console.log("Extracted tokens:", { 
      hasAccess: !!access, 
      hasRefresh: !!refresh,
      hasUserId: !!user_id,
      hasPermissions: !!permissions 
    });

    // Validate that we got the required tokens
    if (!access || !refresh) {
      console.error("Refresh token response missing required tokens. Full response:", {
        responseData,
        originalResponse: response.data,
        access: !!access,
        refresh: !!refresh,
      });
      throw new Error("Invalid response from refresh token endpoint - missing tokens");
    }

    // Update localStorage with new tokens
    const updatedAuthUser = {
      access,
      refresh,
      user_id: user_id || authUser.user_id,
      email: email || authUser.email,
      username: username || authUser.username,
      permissions: permissions || authUser.permissions || [],
    };

    localStorage.setItem("authUser", JSON.stringify(updatedAuthUser));

    // Update Redux state with new user data (including permissions)
    // This ensures the app state is in sync with localStorage
    store.dispatch(loginSuccess({ data: updatedAuthUser }));

    console.log("Token refreshed successfully. New access token obtained.");
    return access;
  } catch (error: any) {
    console.error("Refresh token error:", error);
    console.error("Error details:", {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      code: error.code,
    });

    // Only clear auth data if it's an actual authentication error (401, 403)
    // Don't clear on network errors or other issues
    const errorStatus = error.response?.status || error.response?.data?.status;
    const isAuthError = errorStatus === 401 || errorStatus === 403;
    
    // Also check error message for token-related errors
    const errorMessage = 
      error.response?.data?.message || 
      error.response?.data?.error || 
      error.message || 
      "";
    const isTokenError = 
      errorMessage.toLowerCase().includes("token") ||
      errorMessage.toLowerCase().includes("expired") ||
      errorMessage.toLowerCase().includes("invalid") ||
      errorMessage.toLowerCase().includes("unauthorized");

    // Only clear auth if it's a real authentication/token error
    if (isAuthError || isTokenError) {
      console.warn("Authentication error during token refresh. Clearing auth data.", {
        status: errorStatus,
        message: errorMessage,
      });
      localStorage.removeItem("authUser");
      localStorage.removeItem("userPermissions");
    } else {
      // For other errors (network, server errors, etc.), don't clear auth
      // Just log and throw - let the caller decide what to do
      console.warn("Non-auth error during token refresh. Keeping auth data.", {
        status: errorStatus,
        message: errorMessage,
        error: error,
      });
    }

    // Throw error to be handled by caller
    throw new Error(
      error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to refresh token"
    );
  }
};

/**
 * Handles concurrent refresh requests by queuing them
 * @returns Promise that resolves when token is refreshed
 */
export const handleTokenRefresh = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });

    if (!isRefreshing) {
      isRefreshing = true;
      refreshAccessToken()
        .then((token) => {
          processQueue(null, token);
          isRefreshing = false;
        })
        .catch((error) => {
          processQueue(error, null);
          isRefreshing = false;
        });
    }
  });
};

